// ScenarioLibraryBar.tsx — surfaces the Szenario-Bibliothek (lib/library.ts, previously
// only reachable via the separate #szenarien page) directly in the Cockpit: "Speichern"
// saves the ACTIVE scenario under a name (native window.prompt — one dialog, one field,
// consistent with the native-confirm pattern D-032 also uses for delete/reset, and far
// less UI than a whole inline form for something used occasionally); "Laden" opens a
// short popover list of saved entries, click a name to load it straight into the active
// scenario slot. Both stay reachable from #szenarien too (SzenarienView.tsx) — that page
// additionally offers export/share/import/rename/delete on the library itself; this bar
// is deliberately just the "few clicks, most common case" fast path the user asked for.
import { useState } from 'preact/hooks';
import type { ScenarioState } from '../lib/scenario.js';
import { addToLibrary, loadLibrary, saveLibrary } from '../lib/library.js';
import { formatDateTimeDe } from '../lib/format.js';
import { PopoverMenu } from './PopoverMenu.js';

export interface ScenarioLibraryBarProps {
  activeScenario: ScenarioState;
  onLoad: (scenario: ScenarioState) => void;
  /** Rename the active scenario, so the entered save-name shows on its tab/heading. */
  onRenameActive: (label: string) => void;
}

export function ScenarioLibraryBar({ activeScenario, onLoad, onRenameActive }: ScenarioLibraryBarProps) {
  const [savedFeedback, setSavedFeedback] = useState(false);

  function handleSave() {
    const name = window.prompt('Name für die Bibliothek', activeScenario.label);
    if (name === null) return; // cancelled
    const trimmed = name.trim();
    if (trimmed === '') return;
    // Store the scenario under the entered name AND adopt that name as the active
    // scenario's label, so it's shown correctly everywhere after saving.
    saveLibrary(addToLibrary(loadLibrary(), trimmed, { ...activeScenario, label: trimmed }));
    onRenameActive(trimmed);
    setSavedFeedback(true);
    window.setTimeout(() => setSavedFeedback(false), 2000);
  }

  return (
    <div class="scenario-library-bar">
      <button type="button" class="btn" onClick={handleSave}>
        Speichern
      </button>
      {savedFeedback ? <span class="field-hint scenario-save-feedback">Gespeichert.</span> : null}

      <PopoverMenu label="Gespeichertes Szenario laden" triggerContent="Laden" triggerClass="btn" align="left">
        {(close) => {
          const library = loadLibrary();
          if (library.length === 0) {
            return <p class="field-hint menu-empty-hint">Noch keine gespeicherten Szenarien.</p>;
          }
          return (
            <>
              {library.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  class="menu-item menu-item-library"
                  role="menuitem"
                  onClick={() => {
                    // Load with the library entry's name as the label, so the saved name
                    // shows on the scenario tab/heading.
                    onLoad({ ...entry.scenario, label: entry.name });
                    close();
                  }}
                >
                  <span class="menu-item-library-name">{entry.name}</span>
                  <span class="menu-item-library-date">{formatDateTimeDe(entry.savedAt)}</span>
                </button>
              ))}
            </>
          );
        }}
      </PopoverMenu>
    </div>
  );
}
